import BaseComponent from '@/components/base'
import { prefersPointer } from '@/core/utils'

const selectors = {
  mediaSecondary: '[data-media-secondary]',
  mediaPrimary: '[data-media-primary]',
  productLink: '[data-product-link]',
  colorInput: '[name^="card-color-"]',
  sizeInput: '[name^="card-size-"]',
  variantsData: '[data-card-variants]',
}

const classes = {
  mediaSecondaryReady: 'is-ready'
}

interface CardVariant {
  id: number
  available: boolean
  color: string | null
  size: string
}

export default class ProductCard extends BaseComponent {
  static TYPE = 'product-card'

  mediaSecondary: HTMLElement | null
  primaryImage: HTMLImageElement | null
  secondaryImage: HTMLImageElement | null
  productLinks: HTMLAnchorElement[]
  sizeInputs: HTMLInputElement[]
  variants: CardVariant[]
  baseUrl: string

  constructor(el: HTMLElement) {
    super(el, {
      watchIntersection: true,
    })

    this.mediaSecondary = this.qs(selectors.mediaSecondary) ?? null
    this.primaryImage = (this.qs(`${selectors.mediaPrimary} img`) as HTMLImageElement | undefined) ?? null
    this.secondaryImage = this.mediaSecondary?.querySelector<HTMLImageElement>('img') ?? null
    this.productLinks = this.qsa(selectors.productLink) as HTMLAnchorElement[]
    this.sizeInputs = this.qsa(selectors.sizeInput) as HTMLInputElement[]
    this.baseUrl = this.el.dataset.productUrl || ''

    const dataEl = this.qs(selectors.variantsData)
    this.variants = dataEl?.textContent ? JSON.parse(dataEl.textContent) : []

    this.el.addEventListener('change', this.onChange.bind(this))

    // Sync size availability + product link to the pre-selected colour on load
    this.updateSizeAvailability()
    this.updateProductLink()
  }

  get selectedColor(): string | null {
    return (this.qs(`${selectors.colorInput}:checked`) as HTMLInputElement | undefined)?.value ?? null
  }

  get selectedSize(): string | null {
    return (this.qs(`${selectors.sizeInput}:checked`) as HTMLInputElement | undefined)?.value ?? null
  }

  onChange(e: Event) {
    const input = e.target as HTMLInputElement

    if (input.matches(selectors.colorInput)) {
      this.swapColorImages(input)
      this.updateSizeAvailability()
      this.updateProductLink()
    }
    else if (input.matches(selectors.sizeInput)) {
      this.updateProductLink()
    }
  }

  swapColorImages(input: HTMLInputElement) {
    const imageSrc = input.dataset.imageSrc
    const imageSrcSecondary = input.dataset.imageSrcSecondary

    if (imageSrc && this.primaryImage) {
      this.primaryImage.srcset = ''
      this.primaryImage.src = imageSrc
    }

    if (imageSrcSecondary && this.secondaryImage) {
      // Take over from the lazy loader: set the source directly and stop
      // onIntersection from later overwriting it with the default image.
      this.secondaryImage.srcset = ''
      this.secondaryImage.src = imageSrcSecondary
      this.secondaryImage.removeAttribute('data-src')
      this.secondaryImage.removeAttribute('data-srcset')
      this.mediaSecondary?.classList.add(classes.mediaSecondaryReady)
    }
  }

  /** Grey out + disable sizes that aren't available in the currently selected colour. */
  updateSizeAvailability() {
    if (!this.variants.length || !this.sizeInputs.length) return

    const color = this.selectedColor

    this.sizeInputs.forEach(input => {
      const available = this.variants.some(v => v.color === color && v.size === input.value && v.available)
      input.disabled = !available
    })

    const firstAvailable = this.sizeInputs.find(input => !input.disabled) ?? null

    // If the checked size isn't available in this colour, jump to the first that is
    const checked = this.qs(`${selectors.sizeInput}:checked`) as HTMLInputElement | undefined
    if ((!checked || checked.disabled) && firstAvailable) {
      firstAvailable.checked = true
    }
  }

  /** Point the product link at the exact colour + size variant. */
  updateProductLink() {
    const color = this.selectedColor
    const size = this.selectedSize

    const variant = this.variants.find(v => v.color === color && v.size === size)
      ?? this.variants.find(v => v.color === color)

    let href: string | undefined

    if (variant && this.baseUrl) {
      href = `${this.baseUrl}${this.baseUrl.includes('?') ? '&' : '?'}variant=${variant.id}`
    }
    else {
      // No variant map (colour-only product) — use the checked swatch's own URL
      href = (this.qs(`${selectors.colorInput}:checked`) as HTMLInputElement | undefined)?.dataset.productUrl
    }

    if (!href) return

    this.productLinks.forEach(link => {
      link.href = href!
    })
  }

  onIntersection(entries: IntersectionObserverEntry[]) {
    if (entries[0].isIntersecting) {
      if (this.mediaSecondary instanceof HTMLElement && prefersPointer()) {
        const img = this.mediaSecondary.querySelector('img')

        if (img instanceof HTMLImageElement) {
          img.onload = () => {
            this.mediaSecondary!.classList.add(classes.mediaSecondaryReady)
          }

          if (img.dataset.src) img.src = img.dataset.src
          if (img.dataset.srcset) img.srcset = img.dataset.srcset

          img.removeAttribute('data-src')
          img.removeAttribute('data-srcset')
        }
      }

      this.killIntersectionObserver()
    }
  }
}
